import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { BriefcaseIcon, ExternalLinkIcon, Building2Icon, MapPinIcon, TagIcon, PlusIcon } from 'lucide-react';
import { Link as RouterLink } from '@tanstack/react-router';
import { format } from 'date-fns';
import type { AssignedJob } from '@repo/types';
import { SectionCard } from './_shared';

interface CustomerAssignedJobsProps {
  assignedJobs: AssignedJob[];
  isLoading: boolean;
  onAssignJobClick: () => void;
}

export function CustomerAssignedJobs({ assignedJobs, isLoading, onAssignJobClick }: CustomerAssignedJobsProps) {
  return (
    <SectionCard
      icon={BriefcaseIcon}
      title="Assigned Jobs"
      description={!isLoading ? `${assignedJobs.length} total` : undefined}
      action={
        <Button onClick={onAssignJobClick} size="sm" variant="outline" className="gap-2">
          <PlusIcon className="h-4 w-4" aria-hidden />
          <span className="hidden sm:inline">Assign Job</span>
        </Button>
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : assignedJobs.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-8 text-center text-muted-foreground">
          <BriefcaseIcon className="h-8 w-8 opacity-40" aria-hidden />
          <p className="text-sm">No jobs assigned yet</p>
          <Button onClick={onAssignJobClick} size="sm" variant="outline" className="gap-2">
            <PlusIcon className="h-4 w-4" aria-hidden />
            Assign first job
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {assignedJobs.map((assignment) => (
            <div
              key={assignment.recommendationId}
              className="group rounded-lg border bg-muted/30 p-4 transition-colors hover:border-primary/40 hover:bg-accent"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-2.5">
                  <div className="flex flex-wrap items-center gap-2">
                    <RouterLink
                      to="/admin/jobs/$id"
                      params={{ id: assignment.id }}
                      className="line-clamp-2 text-sm font-semibold transition-colors hover:text-primary"
                    >
                      {assignment.title}
                    </RouterLink>
                    <Badge
                      variant={
                        assignment.recommendationStatus === 'applied'
                          ? 'default'
                          : assignment.recommendationStatus === 'viewed'
                            ? 'secondary'
                            : 'outline'
                      }
                      className="shrink-0 text-xs capitalize"
                    >
                      {assignment.recommendationStatus}
                    </Badge>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                    {assignment.company && (
                      <span className="inline-flex items-center gap-1">
                        <Building2Icon className="h-3.5 w-3.5" aria-hidden />
                        {assignment.company}
                      </span>
                    )}
                    {assignment.location && (
                      <span className="inline-flex items-center gap-1">
                        <MapPinIcon className="h-3.5 w-3.5" aria-hidden />
                        {assignment.location}
                      </span>
                    )}
                    {assignment.category && (
                      <span className="inline-flex items-center gap-1">
                        <TagIcon className="h-3.5 w-3.5" aria-hidden />
                        {assignment.category}
                      </span>
                    )}
                  </div>
                  {assignment.reason && (
                    <p className="rounded-md border-l-2 border-primary bg-muted/50 p-2 text-xs text-muted-foreground">
                      {assignment.reason}
                    </p>
                  )}
                  {(assignment.assignedByName || assignment.assignedAt) && (
                    <p className="text-xs text-muted-foreground">
                      {assignment.assignedByName && (
                        <>
                          Assigned by <span className="font-medium">{assignment.assignedByName}</span>
                        </>
                      )}
                      {assignment.assignedAt && <> on {format(new Date(assignment.assignedAt), 'MMM dd, yyyy')}</>}
                    </p>
                  )}
                </div>
                <RouterLink to="/admin/jobs/$id" params={{ id: assignment.id }} className="shrink-0">
                  <Button variant="ghost" size="icon" aria-label="View job">
                    <ExternalLinkIcon className="h-4 w-4" aria-hidden />
                  </Button>
                </RouterLink>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionCard>
  );
}
